import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import styled, { keyframes } from 'styled-components';
import { FaTimes, FaUserCircle, FaEnvelope, FaIdBadge } from 'react-icons/fa';
import useAccount from '../Hooks/ProfileAccount/useAccount'; 

// Shared theme tokens used across admin UI
// Primary color updated to full 100% green requested (#3fe71d)
export const PROFILE_PRIMARY = '#3fe71d';
export const PROFILE_BG_OVERLAY = 'linear-gradient(180deg, rgba(63,231,29,0.20), rgba(63,231,29,0.45))';
export const GLASS_MIXIN = `
  background: rgba(255, 255, 255, 0.18);
  border: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow: 0 25px 45px rgba(10, 35, 14, 0.4);
  border-radius: 20px;
  backdrop-filter: blur(22px) saturate(130%);
  -webkit-backdrop-filter: blur(22px) saturate(130%);
`;

const formatName = (value) => {
  if (!value) return '';
  return value
    .toString()
    .trim()
    .split(/\s+/)
    .map((word) =>
      word.length ? word.charAt(0).toUpperCase() + word.slice(1).toLowerCase() : ''
    )
    .join(' ');
};

const gradientPulse = keyframes`
  0% {
    transform: rotate(0deg) scale(1);
    opacity: 0.45;
  }
  50% {
    transform: rotate(180deg) scale(1.05);
    opacity: 0.75;
  }
  100% {
    transform: rotate(360deg) scale(1);
    opacity: 0.45;
  }
`;

const softGlow = keyframes`
  0%, 100% {
    box-shadow: 0 18px 32px rgba(26, 111, 37, 0.36);
  }
  50% {
    box-shadow: 0 24px 40px rgba(26, 111, 37, 0.48);
  }
`;

const shimmer = keyframes`
  0% {
    background-position: 0% 50%;
  }
  100% {
    background-position: 100% 50%;
  }
`;

const ProfileModal = ({ onClose }) => {
  const navigate = useNavigate();
  const [isConfirmingLogout, setIsConfirmingLogout] = useState(false);
  const { userData, loading, error } = useAccount(); 

  const cachedName =
    localStorage.getItem('username') ||
    localStorage.getItem('name') ||
    localStorage.getItem('fullname') ||
    localStorage.getItem('user') ||
    '';
  const cachedEmail = localStorage.getItem('email') || '';
  const cachedRole = localStorage.getItem('role') || '';
  const cachedCode = localStorage.getItem('code') || '';

  const rawName =
    (userData && (userData.name || userData.username)) || cachedName;
  const displayName = formatName(rawName) || 'Unknown User';
  const displayEmail = (userData && userData.email) || cachedEmail;
  const displayRole = (userData && userData.role) || cachedRole;
  const displayCode =
    (userData && (userData.code || userData.userId || userData.id)) || cachedCode;

  const handleLogoutClick = () => setIsConfirmingLogout(true);
  const handleConfirmLogout = () => {
    localStorage.removeItem('userId');
    navigate('/');
    onClose();
    setIsConfirmingLogout(false);
  };
  const handleCancelLogout = () => setIsConfirmingLogout(false);

  return (
    <ModalContainer>
      <ModalContent>
        <CloseButton onClick={onClose}>
          <FaTimes style={styles.closeIcon} />
        </CloseButton>

        <Header>
          <AvatarRing>
            <FaUserCircle style={{ fontSize: '46px', color: '#f8fcfa' }} />
          </AvatarRing>
          <HeaderText>
            <Name>{displayName}</Name>
            {displayRole ? <RoleBadge>{displayRole}</RoleBadge> : null}
            <StatusChip>Active session</StatusChip>
          </HeaderText>
        </Header>

        {loading ? (
          <LoadingState>Loading account information…</LoadingState>
        ) : (
          <>
            <InfoSection>
              <SectionTitle>Account details</SectionTitle>
              <InfoList>
                {displayEmail ? (
                  <InfoItem>
                    <InfoIcon>
                      <FaEnvelope />
                    </InfoIcon>
                    <InfoText>
                      <InfoLabel>Email</InfoLabel>
                      <InfoValue title={displayEmail}>{displayEmail}</InfoValue>
                    </InfoText>
                  </InfoItem>
                ) : null}
                {displayCode ? (
                  <InfoItem>
                    <InfoIcon>
                      <FaIdBadge />
                    </InfoIcon>
                    <InfoText>
                      <InfoLabel>Account Code</InfoLabel>
                      <InfoValue>{displayCode}</InfoValue>
                    </InfoText>
                  </InfoItem>
                ) : null}
              </InfoList>
              {error ? (
                <Hint>
                  Showing cached information. Please refresh if this seems outdated.
                </Hint>
              ) : null}
            </InfoSection>

            <FooterNote>
              Need to update your profile? Contact your system administrator.
            </FooterNote>
          </>
        )}

        <LogoutButton onClick={handleLogoutClick}>Logout</LogoutButton>
      </ModalContent>

      {isConfirmingLogout && (
        <ConfirmationModal>
          <ConfirmationContent>
            <p>Are you sure you want to log out?</p>
            <ConfirmationButtons>
              <ConfirmButton onClick={handleConfirmLogout}>Yes</ConfirmButton>
              <CancelButton onClick={handleCancelLogout}>No</CancelButton>
            </ConfirmationButtons>
          </ConfirmationContent>
        </ConfirmationModal>
      )}
    </ModalContainer>
  );
};

export default ProfileModal;


const SIDEBAR_COLLAPSED_WIDTH = 88;

const ModalContainer = styled.div`
  position: fixed;
  top: clamp(28px, 4vh, 48px);
  right: clamp(12px, 3vw, 24px);
  width: clamp(250px, 26vw, 320px);
  min-height: 380px;
  max-height: calc(100vh - clamp(48px, 12vh, 92px));
  display: flex;
  justify-content: flex-end;
  align-items: stretch;
  background: radial-gradient(circle at top, rgba(24, 89, 29, 0.35), rgba(4, 12, 6, 0.65));
  border-radius: 22px;
  padding: 6px;
  z-index: 1400;
  backdrop-filter: blur(6px);
  overflow: hidden;

  @media (max-width: 1024px) {
    right: clamp(10px, 4vw, 28px);
    width: clamp(240px, 42vw, 320px);
  }

  @media (max-width: 768px) {
    left: calc(${SIDEBAR_COLLAPSED_WIDTH}px + clamp(10px, 6vw, 32px));
    right: clamp(16px, 8vw, 40px);
    width: auto;
    max-width: calc(100vw - ${SIDEBAR_COLLAPSED_WIDTH}px - clamp(26px, 14vw, 72px));
    margin: 0 auto;
    justify-content: center;
    border-radius: 20px;
  }

  @media (max-width: 560px) {
    left: calc(${SIDEBAR_COLLAPSED_WIDTH}px + 12px);
    right: clamp(14px, 8vw, 28px);
    max-width: calc(100vw - ${SIDEBAR_COLLAPSED_WIDTH}px - clamp(26px, 16vw, 60px));
  }

  @media (max-width: 480px) {
    left: calc(${SIDEBAR_COLLAPSED_WIDTH}px + 10px);
    right: 16px;
    max-width: calc(100vw - ${SIDEBAR_COLLAPSED_WIDTH}px - 26px);
    padding: 12px;
    border-radius: 18px;
    background: rgba(4, 12, 6, 0.78);
    backdrop-filter: blur(12px);
    justify-content: center;
    align-items: center;
  }

  @media (max-width: 380px) {
    left: calc(${SIDEBAR_COLLAPSED_WIDTH}px + 8px);
    right: 12px;
    max-width: calc(100vw - ${SIDEBAR_COLLAPSED_WIDTH}px - 20px);
  }
`;

const ModalContent = styled.div`
  ${GLASS_MIXIN}
  padding: clamp(20px, 4vw, 26px);
  width: 100%;
  max-width: 400px;
  max-height: 100%;
  display: flex;
  flex-direction: column;
  gap: clamp(18px, 3vw, 26px);
  position: relative;
  overflow-y: auto;
  scrollbar-width: none;
  -ms-overflow-style: none;
  &::-webkit-scrollbar { display: none; }
  border-radius: clamp(16px, 4vw, 22px);
  box-shadow: 0 24px 42px rgba(10, 35, 14, 0.42);
  @media (max-width: 768px) {
    gap: clamp(18px, 5vw, 24px);
  }
  @media (max-width: 480px) {
    max-height: calc(100vh - 48px);
    padding: 22px 20px 18px;
  }
  &::before {
    content: '';
    position: absolute;
    inset: -45% -60% -55% -40%;
    background: conic-gradient(from 45deg, rgba(26, 111, 37, 0.4), rgba(248, 252, 250, 0.08), rgba(46, 156, 58, 0.5), rgba(4, 12, 6, 0.15));
    filter: blur(50px);
    animation: ${gradientPulse} 18s ease-in-out infinite;
    pointer-events: none;
  }
  & > * { position: relative; z-index: 1; }
`;

const Header = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 14px;
  background: rgba(255, 255, 255, 0.12);
  border-radius: 18px;
  padding: 14px 16px;
  border: 1px solid rgba(255, 255, 255, 0.18);
  @media (max-width: 480px) {
    align-items: center;
    gap: 12px;
  }
`;
const AvatarRing = styled.div`
  width: 72px;
  height: 72px;
  margin: 0 auto;
  border-radius: 50%;
  background: linear-gradient(135deg, rgba(26, 111, 37, 0.95), rgba(46, 156, 58, 0.7));
  display: flex;
  align-items: center;
  justify-content: center;
  border: 1px solid rgba(255, 255, 255, 0.3);
  box-shadow: 0 18px 32px rgba(26, 111, 37, 0.4);
  animation: ${softGlow} 6s ease-in-out infinite;
  @media (max-width: 480px) {
    width: 64px;
    height: 64px;
  }
`;

const HeaderText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  align-items: center;
  text-align: center;
  width: 100%;
`;

const Name = styled.h2`
  margin: 0;
  color: #f2fff6;
  font-weight: 700;
  font-size: clamp(16px, 1.9vw, 20px);
`;

const RoleBadge = styled.span`
  display: inline-block;
  padding: 4px 12px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.32);
  color: #13481b;
  font-weight: 600;
  font-size: 12px;
  letter-spacing: 0.5px;
  text-transform: uppercase;
`;

const StatusChip = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  font-size: 11px;
  color: rgba(248, 252, 250, 0.82);
  letter-spacing: 0.4px;

  &::before {
    content: '';
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: linear-gradient(135deg, #40c463, #1a6f25);
    box-shadow: 0 0 6px rgba(64, 196, 99, 0.7);
  }
`;

const LoadingState = styled.p`
  margin: 0;
  font-size: 13px;
  color: rgba(242, 255, 246, 0.85);
  text-align: center;
`;

const InfoSection = styled.section`
  display: flex;
  flex-direction: column;
  gap: 14px;
`;

const SectionTitle = styled.h3`
  margin: 0;
  font-weight: 700;
  color: #1f4024;
  text-transform: uppercase;
  letter-spacing: 0.6px;
  font-size: clamp(12px, 1.4vw, 13px);
`;

const InfoList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
`;

const InfoItem = styled.div`
  display: flex;
  align-items: center;
  gap: clamp(10px, 2vw, 14px);
  padding: clamp(10px, 2.4vw, 14px);
  border-radius: clamp(12px, 3vw, 16px);
  background: rgba(255, 255, 255, 0.18);
  border: 1px solid rgba(255, 255, 255, 0.2);
  box-shadow: 0 10px 18px rgba(16, 61, 21, 0.2);
  @media (max-width: 480px) {
    align-items: flex-start;
  }
`;

const InfoIcon = styled.span`
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: rgba(26, 111, 37, 0.22);
  color: #f1fff5;
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 16px;
  border: 1px solid rgba(255, 255, 255, 0.2);

  @media (max-width: 480px) {
    width: 32px;
    height: 32px;
    font-size: 14px;
  }
`;

const InfoText = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2px;
`;

const InfoLabel = styled.span`
  font-size: 11px;
  color: rgba(228, 245, 233, 0.75);
  letter-spacing: 0.5px;
  text-transform: uppercase;
`;

const InfoValue = styled.span`
  font-weight: 600;
  color: #f8fcfa;
  word-break: break-word;
  font-size: clamp(13px, 1.8vw, 15px);
`;

const Hint = styled.p`
  margin: 4px 0 0;
  font-size: 11px;
  color: rgba(230, 240, 233, 0.65);
`;

const FooterNote = styled.p`
  margin: 0;
  text-align: center;
  border-radius: clamp(12px, 3vw, 14px);
  padding: clamp(10px, 2.6vw, 14px);
  background: linear-gradient(135deg, rgba(26, 111, 37, 0.18), rgba(46, 156, 58, 0.12));
  border: 1px solid rgba(255, 255, 255, 0.18);
  color: rgba(248, 252, 250, 0.82);
  font-size: clamp(11px, 1.6vw, 13px);
`;

const LogoutButton = styled.button`
  width: 100%;
  margin-top: auto;
  padding: clamp(11px, 2.6vw, 14px);
  font-size: clamp(13px, 1.8vw, 15px);
  border-radius: clamp(12px, 3vw, 14px);
  background: linear-gradient(135deg, rgba(26, 111, 37, 0.95), rgba(46, 156, 58, 0.75));
  color: #f8fcfa;
  border: none;
  cursor: pointer;
  box-shadow: 0 12px 24px rgba(26, 111, 37, 0.35);
  transition: transform 0.25s ease, box-shadow 0.25s ease;
  position: relative;
  overflow: hidden;
  &::after {
    content: '';
    position: absolute;
    inset: -120% -40%;
    background: linear-gradient(120deg, rgba(255, 255, 255, 0.35) 0%, rgba(255, 255, 255, 0) 45%);
    transform: translateX(-50%);
    animation: ${shimmer} 2.8s linear infinite;
    pointer-events: none;
  }
  &:hover { transform: translateY(-2px); box-shadow: 0 16px 30px rgba(26, 111, 37, 0.45); }
  &:active { transform: translateY(0); }
`;

const CloseButton = styled.button`
  position: absolute;
  top: 12px;
  right: 12px;
  background-color: transparent;
  color: rgba(248, 252, 250, 0.85);
  font-size: 12px;
  border: none;
  padding: 4px;
  cursor: pointer;
  border-radius: 50%;
  transition: background-color 0.2s ease, color 0.2s ease;
  &:hover {
    background-color: rgba(244, 247, 246, 0.18);
    color: #ffffff;
  }
`;

const ConfirmationModal = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  height: 100%;
  width: 100%;
  background-color: rgba(0, 0, 0, 0.5);
  display: flex;
  justify-content: center;
  align-items: center;
  z-index: 1000;

  @media (max-width: 480px) {
    padding: 16px;
  }
`;

const ConfirmationContent = styled.div`
  background-color: #fff;
  padding: 30px;
  border-radius: 10px;
  text-align: center;
  box-shadow: 0 4px 6px rgba(0, 0, 0, 0.1);

  @media (max-width: 768px) {
    width: min(360px, 90vw);
  }

  @media (max-width: 480px) {
    padding: 24px 20px;
  }
`;

const ConfirmationButtons = styled.div`
  display: flex;
  justify-content: center;
  gap: 15px;
  margin-top: 20px;
`;

const ConfirmButton = styled.button`
  background-color: #18591d;
  color: #f8fcfa;
  border: none;
  padding: 10px 20px;
  cursor: pointer;
  font-size: 18px;
  border-radius: 5px;
  &:hover {
    background-color: #3b9a53;
  }
`;

const CancelButton = styled.button`
  background-color: #f8fcfa;
  color: #18591d;
  border: 1px solid #18591d;
  padding: 10px 20px;
  cursor: pointer;
  font-size: 18px;
  border-radius: 5px;
  &:hover {
    background-color: #e0f7e0;
  }
`;

const styles = {
  closeIcon: {
    fontSize: '18px',
  }
};
